import { observable } from "@legendapp/state";
import {
  listAdvertisersByAgencies,
  listAdvertisersByOrganization,
} from "@/apis/clients/advertisersClient";
import { sortedIdKey, toErrorMessage } from "@/stores/storeHelpers";
import type { Advertiser } from "@/types/advertiser";
import { sortByName } from "@/utils/sortOptions";

interface AdvertiserScope {
  organizationId: number;
  agencyIds: number[];
}

interface AdvertisersState {
  advertisers: Advertiser[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
  advertiserInitialized: boolean;
}

const defaultValues: AdvertisersState = {
  advertisers: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
  advertiserInitialized: false,
};

export const AdvertisersStore$ = observable(defaultValues);

// Agencies, when any are selected, are the more specific filter; otherwise
// fall back to the full org-wide list.
const getScopeKey = ({ organizationId, agencyIds }: AdvertiserScope) => {
  if (agencyIds.length > 0) {
    return `agency:${sortedIdKey(agencyIds)}`;
  }
  return `organization:${organizationId}`;
};

const fetchAdvertisersForScope = async ({
  organizationId,
  agencyIds,
}: AdvertiserScope) => {
  if (agencyIds.length > 0) {
    return listAdvertisersByAgencies(agencyIds);
  }
  return listAdvertisersByOrganization(organizationId);
};

// A scope change (e.g. selecting an agency) can arrive while a previous,
// slower fetch (e.g. the initial org-wide, multi-page load) is still in
// flight. Gating on `isLoading` would silently drop the newer request with
// no retry, leaving stale data on screen — so instead every call is allowed
// to start, and a request token ensures only the most recent one's result
// ever gets committed.
let latestRequestId = 0;

export const fetchAdvertisers = async (scope: AdvertiserScope) => {
  const scopeKey = getScopeKey(scope);

  if (AdvertisersStore$.loadedForScopeKey.get() === scopeKey) {
    return;
  }

  latestRequestId += 1;
  const requestId = latestRequestId;
  AdvertisersStore$.assign({ isLoading: true, error: null });

  try {
    const advertisers = sortByName(await fetchAdvertisersForScope(scope));
    if (requestId !== latestRequestId) return;

    AdvertisersStore$.assign({
      advertisers,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (requestId !== latestRequestId) return;
    AdvertisersStore$.assign({
      advertisers: [],
      error: toErrorMessage(error),
      loadedForScopeKey: null,
    });
  } finally {
    if (requestId === latestRequestId) {
      AdvertisersStore$.isLoading.set(false);
    }
  }
};

export const clearAdvertisers = () => {
  // Increment the request token so any in-flight fetch is treated as superseded
  // and its result discarded when it arrives.
  latestRequestId += 1;
  AdvertisersStore$.assign({
    advertisers: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
    advertiserInitialized: false,
  });
};
